import React, { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/Layout";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { BarChart3 } from "lucide-react";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";

export default function LabAnalytics() {
  const [reports, setReports] = useState([]);
  useEffect(() => { api.get("/reports?lite=1").then((r) => setReports(r.data)); }, []);

  // Report volume per day (last 14 days)
  const volume = useVolume(reports);
  // Most flagged biomarkers
  const flagCounts = {};
  for (const r of reports) {
    for (const b of r.smart_report?.critical_findings || []) {
      if (!b?.name) continue;
      flagCounts[b.name] = (flagCounts[b.name] || 0) + 1;
    }
  }
  const flags = Object.entries(flagCounts).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([name, value]) => ({ name, value }));
  const colors = ["#E55A25", "#F5C400", "#4CAF50", "#5A5AC7", "#E53935", "#1565C0"];

  return (
    <DashboardLayout role="lab_admin">
      <h1 className="font-heading font-bold text-3xl text-ink flex items-center gap-2"><BarChart3 className="w-6 h-6 text-efgh-blue" /> Analytics</h1>
      <p className="text-ink/60">Understand patient engagement and revenue impact.</p>

      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        <Card className="p-6 rounded-2xl">
          <div className="font-heading font-semibold text-ink">Reports per day — last 14 days</div>
          <div className="mt-3 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={volume}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EEF0F4" />
                <XAxis dataKey="d" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={26} />
                <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="v" fill="#5A5AC7" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6 rounded-2xl">
          <div className="font-heading font-semibold text-ink">Most flagged biomarkers</div>
          {flags.length === 0 ? (
            <div className="mt-8 text-center text-ink/50 text-sm">No critical findings across processed reports yet.</div>
          ) : (
            <div className="mt-3 h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={flags} innerRadius={50} outerRadius={90} paddingAngle={2} dataKey="value" nameKey="name">
                    {flags.map((_, i) => <Cell key={i} fill={colors[i % colors.length]} />)}
                  </Pie>
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>
      </div>
    </DashboardLayout>
  );
}

function useVolume(reports) {
  const bins = {};
  const now = new Date();
  for (let i = 13; i >= 0; i--) {
    const d = new Date(now); d.setDate(now.getDate() - i);
    bins[d.toISOString().slice(0, 10)] = 0;
  }
  for (const r of reports) {
    const k = (r.created_at || "").slice(0, 10);
    if (k in bins) bins[k]++;
  }
  return Object.entries(bins).map(([k, v]) => ({ d: k.slice(5), v }));
}
